import { Camera } from '@babylonjs/core/Cameras/camera';
import { PassPostProcess } from '@babylonjs/core/PostProcesses/passPostProcess';
import { FxaaPostProcess } from '@babylonjs/core/PostProcesses/fxaaPostProcess';
import { ImageProcessingConfiguration } from '@babylonjs/core/Materials/imageProcessingConfiguration';
import { ColorCurves } from '@babylonjs/core/Materials/colorCurves';
import clone from 'lodash/clone';

import { SystemLayer } from '../System/systemLayer';
import { projectInterface } from '../Tool/interface';

export interface IPipelineOptions {
    fieldOfView?: number,
    contrast?: number,
    exposure?: number,
    colorMapping?: number[],
}

/**
 * @ignore Property Rights, ThreedJson
 */
export const postprocessJson = {
    fieldOfView: 'f',
    contrast: 'c',
    exposure: 'e',
    colorMapping: 'cm',
};

export const pipelineJson = postprocessJson;

projectInterface.pipeline = pipelineJson;

export const defaultPipelineValues: IPipelineOptions = {
    fieldOfView: 0.4,
    contrast: 1,
    exposure: 1,
    colorMapping: [30, 0, 0, 0],
};

/**
* Manager of all story postprocesses, the pipeline will not impact UI (Progress bar, etc)
*/

export class SceneryPipeline {
    /**
    * @ignore
    */
    private _system: SystemLayer;

    public currentValues: IPipelineOptions = clone(defaultPipelineValues);

    /**
    * @param system System of the 3D scene
    */
    constructor(system: SystemLayer) {
        this._system = system;
        this.initSceneImageProcessing();
    }

    private imageProcConfig: ImageProcessingConfiguration;

    private initSceneImageProcessing() {
        this.imageProcConfig = this._system.scene.imageProcessingConfiguration;
        this.imageProcConfig.isEnabled = true;
        this.imageProcConfig.applyByPostProcess = false;
        // Need to reset it to false again as something changes it to true
        this.imageProcConfig.isEnabled = true;
        this.imageProcConfig.colorCurvesEnabled = true;
        this.imageProcConfig.colorCurves = new ColorCurves();
        this.imageProcConfig.vignetteEnabled = false;
        this.addCamera(this._system.arcCamera);
    }

    public setCurrentOption(pipelineOptions: IPipelineOptions): void {
        const keys = Object.keys(pipelineOptions);
        keys.forEach((key) => {
            this.currentValues[key] = clone(pipelineOptions[key]);
        });
    }

    public getCurrentOption(): IPipelineOptions {
        return { ...this.currentValues };
    }

    /**
    * Set all the pipeline options
    * @param pipelineOptions The options to be set
    */
    public setOption(pipelineOptions: IPipelineOptions): void {
        this.setFieldOfView(pipelineOptions.fieldOfView);
        this.setContrast(pipelineOptions.contrast);
        this.setExposure(pipelineOptions.exposure);
        this.setColorMapping(pipelineOptions.colorMapping);
        this.setCurrentOption(pipelineOptions);
    }

    /**
    * Set only some pipeline options
    * @param pipelineOptions The options to be set
    */
    public checkOption(pipOptions: IPipelineOptions): void {
        if (pipOptions.fieldOfView !== undefined) this.setFieldOfView(pipOptions.fieldOfView);
        if (pipOptions.contrast !== undefined) this.setContrast(pipOptions.contrast);
        if (pipOptions.exposure !== undefined) this.setExposure(pipOptions.exposure);
        if (pipOptions.colorMapping !== undefined) this.setColorMapping(pipOptions.colorMapping);
        this.setCurrentOption(pipOptions);
    }

    /**
    * Set the field of view
    * @param fieldOfView of the scene camera
    */
    public setFieldOfView(fieldOfView: number): void {
        if (!fieldOfView) fieldOfView = defaultPipelineValues.fieldOfView;
        // All cameras need the good field of view for pick events and position for instance
        this.cameras.forEach((camera) => {
            camera.fov = fieldOfView;
        });
    }

    /**
    * Set the contrast postprocess
    * @param contrast Contrast of the scene
    */
    public setContrast(contrast: number): void {
        if (contrast === undefined || contrast === null) contrast = defaultPipelineValues.contrast;
        this.imageProcConfig.contrast = contrast;
    }

    /**
    * Set the exposure postprocess
    * @param exposure Exposure of the scene
    */
    public setExposure(exposure: number): void {
        if (exposure === undefined || exposure === null) exposure = defaultPipelineValues.exposure;
        this.imageProcConfig.exposure = exposure;
    }

    public setColorMapping(colorMapping: number[]): void {
        // From 0 to 360 in babylonJS but better to have -180 to 180 input
        if (!colorMapping) colorMapping = defaultPipelineValues.colorMapping;
        const { colorCurves } = this.imageProcConfig;
        colorCurves.globalHue = colorMapping[0] + 180;
        colorCurves.globalSaturation = colorMapping[1];
        colorCurves.globalExposure = colorMapping[2];
        colorCurves.globalDensity = colorMapping[3];
    }

    /**
    * List of the cameras used by pipeline
    */
    private cameras: Camera[] = [];

    /**
    * @param camera Camera to be added in pipeline
    */
    public addCamera(camera: Camera): void {
        // console.log('add', camera);
        const index = this.cameras.indexOf(camera);
        if (index === -1) {
            this.cameras.push(camera);
            if (this._system.engine.webGLVersion === 2) {
                const msaa = new PassPostProcess('MSAA', 1.0, camera);
                msaa.samples = 4;
            } else {
                const fxaa = new FxaaPostProcess('FXAA', 1.0, camera);
            }
        }
        this.checkOption(this.currentValues);
    }

    /**
    * @param camera Camera to be removed in pipeline
    */
    public removeCamera(camera: Camera): void {
        // console.log('remove', camera);
        const index = this.cameras.indexOf(camera);
        if (index !== -1) {
            this.cameras.splice(index, 1);
        }
    }
}
